import React, { useEffect, useMemo, useState } from 'react';
import { Form, Button, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { TournamentSummary } from '../../../types/types';
import Modal from '../../common/Modal';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { styled } from 'styled-components';
import { COLORS, TEXT_SIZES } from '../../../utils/constants';
import {
  updateTournamentRequest,
  getTournamentBracketRequest,
} from '../../../api/requests/tournaments';
import {
  StyledInput,
  BlueCalendarIcon,
  ModalContent,
  StyledDatePicker,
  StyledFormItem,
} from './SinglesBrackets';

interface Props {
  visible: boolean;
  onClose: () => void;
  onSave?: () => void;
  tournamentId: number;
  eventId: number;
  eventStartDate?: string;
  eventEndDate?: string;
  eventRegistrationStartDate?: string;
  eventRegistrationEndDate?: string;
  tournament: TournamentSummary;
  reloadEvent: () => Promise<void>;
}

const TournamentEditModal: React.FC<Props> = ({
  visible,
  onClose,
  tournament,
  tournamentId,
  eventStartDate,
  eventEndDate,
  eventRegistrationStartDate,
  eventRegistrationEndDate,
  reloadEvent,
}) => {
  const [form] = Form.useForm();
  const snackbar = useSnackbar();
  const [isPending, setIsPending] = useState<boolean>(false);
  const [dateError, setDateError] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  // Registration dates lock once any match has started (active scoring phase).
  const [registrationDatesLocked, setRegistrationDatesLocked] =
    useState<boolean>(false);

  const initialValues = useMemo(
    () => ({
      entryFee: tournament.entryFee?.toString() || '',
      startDate: tournament.startDate ? dayjs(tournament.startDate) : undefined,
      endDate: tournament.endDate ? dayjs(tournament.endDate) : undefined,
      registrationStartDate: tournament.registrationStartDate
        ? dayjs(tournament.registrationStartDate)
        : eventRegistrationStartDate
          ? dayjs(eventRegistrationStartDate)
          : undefined,
      registrationEndDate: tournament.registrationEndDate
        ? dayjs(tournament.registrationEndDate)
        : eventRegistrationEndDate
          ? dayjs(eventRegistrationEndDate)
          : undefined,
    }),
    [tournament, eventRegistrationStartDate, eventRegistrationEndDate],
  );

  useEffect(() => {
    if (visible) {
      form.setFieldsValue(initialValues);
      setDateError('');
    }
  }, [visible, initialValues, form]);

  // Determine whether registration dates are locked: only possible once a bracket
  // exists, so skip the lookup otherwise. A match in progress/completed locks the dates.
  useEffect(() => {
    let cancelled = false;
    const checkRegistrationLock = async () => {
      if (!visible || !tournament.bracketGenerated) {
        setRegistrationDatesLocked(false);
        return;
      }
      try {
        const response = await getTournamentBracketRequest(tournamentId);
        const rounds = response?.value?.rounds ?? [];
        const anyMatchStarted = rounds.some((round: any) =>
          round.matchups?.some(
            (m: any) =>
              m.match?.statusName === 'InProgress' ||
              m.match?.statusName === 'Completed',
          ),
        );
        if (!cancelled) setRegistrationDatesLocked(anyMatchStarted);
      } catch {
        // On failure, leave dates editable; the API still enforces the rule on save.
        if (!cancelled) setRegistrationDatesLocked(false);
      }
    };
    checkRegistrationLock();
    return () => {
      cancelled = true;
    };
  }, [visible, tournament.bracketGenerated, tournamentId]);

  const eventStartDayJS = useMemo(
    () => (eventStartDate ? dayjs(eventStartDate).startOf('day') : undefined),
    [eventStartDate],
  );

  const eventEndDayJS = useMemo(
    () => (eventEndDate ? dayjs(eventEndDate).endOf('day') : undefined),
    [eventEndDate],
  );

  const handleSave = async () => {
    setHasSubmitted(true);
    try {
      setIsPending(true);
      const values = await form.validateFields();
      const startDate: Dayjs = values.startDate.startOf('day');
      const endDate: Dayjs = values.endDate.endOf('day');
      const registrationStartDate: Dayjs | undefined = values.registrationStartDate?.startOf('day');
      const registrationEndDate: Dayjs | undefined = values.registrationEndDate?.endOf('day');

      if (!startDate || !endDate) {
        setDateError('Please select valid start and end dates');
        return;
      }
      if (endDate.isBefore(startDate)) {
        setDateError('End date cannot be earlier than start date');
        return;
      }
      if (registrationEndDate && registrationEndDate.isAfter(startDate)) {
        setDateError('Registration end date must be before tournament start date');
        return;
      }

      const payload = {
        tournamentId,
        entryFee: Number(values.entryFee),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        registrationStartDate: registrationStartDate?.toISOString() ?? null,
        registrationEndDate: registrationEndDate?.toISOString() ?? null,
        tournamentName: tournament.tournamentName,
        tournamentType: tournament.tournamentType,
        customTournamentCategory: tournament.customTournamentCategory,
        gender: tournament.tournamentGender,
        rules: '',
        prizeStructure: '',
      };
      const response = await updateTournamentRequest(payload);
      if (response.isSuccess) {
        snackbar.success('Tournament updated successfully');
        await reloadEvent();
        onClose();
      } else {
        snackbar.error('Tournament update failed');
      }
    } catch (err) {
      const errorMessage = (err as any)?.Error || 'Failed to update tournament';
      snackbar.error(errorMessage);
    } finally {
      setIsPending(false);
    }
  };

  const handleCancel = () => {
    form.setFieldsValue(initialValues);
    setDateError('');
    onClose();
  };

  const tournamentName = useMemo(
    () =>
      `${tournament.sportName}-${tournament.tournamentGenderName}-${tournament.customTournamentCategory}-${tournament.tournamentTypeName}`,
    [tournament],
  );

  return (
    <Modal
      title={tournamentName}
      open={visible}
      onCancel={handleCancel}
      width={500}
      minHeight="auto"
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isPending}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={isPending}
          disabled={isPending}
        >
          Save
        </Button>,
      ]}
    >
      <ModalContent>
        <Form
          form={form}
          layout="vertical"
          validateTrigger={hasSubmitted ? 'onChange' : 'onSubmit'}
        >
          <StyledFormItem
            name="entryFee"
            label="Tournament Fee"
            rules={[{ required: true, message: 'Please enter fee' }]}
          >
            <StyledInput
              type="number"
              placeholder="Enter fee"
              min="0"
              step="1"
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e' || e.key === '+') {
                  e.preventDefault();
                }
              }}
            />
          </StyledFormItem>
          <StyledFormItem
            name="registrationStartDate"
            label="Registration Start Date"
          >
            <StyledDatePicker
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              suffixIcon={<BlueCalendarIcon />}
              disabled={registrationDatesLocked}
              disabledDate={(date: Dayjs) => {
                if (!date) return false;
                if (eventStartDayJS && date.isBefore(eventStartDayJS, 'day')) return true;
                if (eventEndDayJS && date.isAfter(eventEndDayJS, 'day')) return true;
                return false;
              }}
              onChange={() => {
                form.setFieldsValue({ registrationEndDate: undefined });
              }}
            />
          </StyledFormItem>
          <StyledFormItem
            name="registrationEndDate"
            label="Registration End Date"
          >
            <StyledDatePicker
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              suffixIcon={<BlueCalendarIcon />}
              disabled={registrationDatesLocked}
              disabledDate={(date: Dayjs) => {
                if (!date) return false;
                const regStart = form.getFieldValue('registrationStartDate');
                if (regStart && date.isBefore(regStart, 'day')) return true;
                const tournamentStart = form.getFieldValue('startDate');
                if (tournamentStart && !date.isBefore(tournamentStart, 'day')) return true;
                if (eventEndDayJS && date.isAfter(eventEndDayJS, 'day')) return true;
                return false;
              }}
            />
          </StyledFormItem>
          {registrationDatesLocked && (
            <RegistrationLockHint>
              Registration dates cannot be changed because matches have already
              started.
            </RegistrationLockHint>
          )}
          <StyledFormItem
            name="startDate"
            label="Tournament Start Date"
            rules={[{ required: true, message: 'Select start date' }]}
          >
            <StyledDatePicker
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              suffixIcon={<BlueCalendarIcon />}
              disabledDate={(date: Dayjs) => {
                if (!date) return false;
                const regEnd = form.getFieldValue('registrationEndDate');
                if (regEnd && !date.isAfter(regEnd, 'day')) return true;
                if (eventStartDayJS && date.isBefore(eventStartDayJS, 'day')) return true;
                if (eventEndDayJS && date.isAfter(eventEndDayJS, 'day')) return true;
                return false;
              }}
            />
          </StyledFormItem>
          <StyledFormItem
            name="endDate"
            label="Tournament End Date"
            rules={[{ required: true, message: 'Select end date' }]}
          >
            <StyledDatePicker
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              suffixIcon={<BlueCalendarIcon />}
              disabledDate={(date: Dayjs) => {
                if (!date) return false;
                const startDate = form.getFieldValue('startDate');
                if (startDate && date.isBefore(startDate, 'day')) return true;
                if (eventEndDayJS && date.isAfter(eventEndDayJS, 'day')) return true;
                return false;
              }}
            />
          </StyledFormItem>
          {dateError && <DateError>{dateError}</DateError>}
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default TournamentEditModal;

const DateError = styled(Typography.Text)`
  color: ${COLORS.red.primary};
  font-size: ${TEXT_SIZES.SM}px;
  margin-top: 8px;
`;

const RegistrationLockHint = styled(Typography.Text)`
  color: ${COLORS.grey.dark};
  font-size: ${TEXT_SIZES.XS}px;
  margin-top: 4px;
`;
